import { Component } from 'react';
import cx from 'classnames';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { Button } from '@fff-web/fff-ui-shared';
import withUniversalNavigationContext from '../../../hoc/withUniversalNavigationContext';
import analytics, { events } from '../../../utils/analytics';
import boxStates from '../../../utils/boxStates';
import { boxExpired } from '../../../utils/boxStatus';
import {
  campaignState,
  getCustomizeOpenStatus,
  getNMCOpenStatus,
} from '../../../utils/campaignState';
import env from '../../../utils/env';
import { scrollToComponent } from '../../../utils/scroll-to-component';
import { isClubMember, isSelectMember } from '../../../utils/user';
import NoItemsCTA from '../SharedComponents/NoItemsCTA/NoItemsCTA';
import PageNav from '../SharedComponents/PageNav/PageNav';
import ReactivateCTA from '../SharedComponents/ReactivateCTA/ReactivateCTA';
import ClosedNonParticipant from './ClosedNonParticipant';
import Pre from './CustomizeStates/Pre';
import NotExpiredOpenParticipant from './NotExpiredOpenParticipant';
import * as classNames from './CustomizePanel.module.css';

class CustomizeView extends Component {
  static get propTypes() {
    return {
      sysdata: shape({
        boxData: shape({
          customizeCampaignState: shape({}).isRequired,
          customizations: arrayOf(
            shape({
              additionalItems: arrayOf(
                shape({
                  quantity: number.isRequired,
                  price: number.isRequired,
                }),
              ).isRequired,
              questionType: string.isRequired,
              additionalItemCost: number.isRequired,
            }),
          ).isRequired,
        }).isRequired,
        userData: shape({}).isRequired,
        isMobile: bool.isRequired,
        revealCallback: func.isRequired,
      }).isRequired,
      universalNavigationContext: shape({
        isNewMember: bool.isRequired,
      }).isRequired,
    };
  }

  state = {
    viewAll: false,
    currentPage: 1,
    pageLength: 5, // @TODO Get this number from the CMS
    initialDisplayAmount: 6, // @TODO Get this number from the CMS,
  };

  getExtraSum = (boxData) => {
    if (!boxData || !boxData.customizations) {
      return 0;
    }

    let total = 0;
    const questions = boxData.customizations;
    questions.forEach((question) => {
      if (question.additionalItems) {
        question.additionalItems.forEach((item) => (total += item.additionalQuantity * item.price));
      }
      if (question.choice) {
        if (question.choice.additionalQuantity) {
          total += question.choice.additionalQuantity * question.choice.price;
        }
        // add premium items
        if (question.choice.premiumPrice) {
          total += question.choice.primaryQuantity * question.choice.premiumPrice;
        }
      }
    });

    return total;
  };

  getOpenStatus = (boxData) => {
    const { universalNavigationContext } = this.props;
    const { isNewMember } = universalNavigationContext;
    return isNewMember ? getNMCOpenStatus() : getCustomizeOpenStatus(boxData);
  };

  getExtraChoiceCount = (boxData) => {
    let total = 0;
    const questions = boxData.customizations;

    for (const question of questions) {
      if (question.additionalItems) {
        question.additionalItems.forEach((item) => (total += item.additionalQuantity));
      }
      if (question.choice) {
        if (question.choice.additionalQuantity) {
          total += question.choice.additionalQuantity;
        }
        // count premium items
        if (question.choice.premiumPrice) {
          total += question.choice.primaryQuantity;
        }
      }
    }

    return total;
  };

  getItems = (boxData, userData) => {
    const questions = boxData.customizations;
    if (!questions) {
      return [];
    }
    const items = [];
    const isSelect = isSelectMember(userData);

    for (const question of questions) {
      const grouping = question.questionType;
      const { questionId } = question;
      const extraChoicePrice = question.additionalItemCost;

      if (questionId >= 9) continue;

      const surpriseItem = {
        id: 0,
        image:
          'https://static.fabfitfun.com/wp-content/uploads/2020/02/icon-surprise-me%402x_1582677417.6491_1582677417.9295.jpg',
        title: 'Mystery Surprise',
        questionId,
        grouping,
      };

      if (!isSelect && grouping === 'select') {
        items.push(surpriseItem);
      } else if (
        (!question.choice && !question.additionalItems) ||
        (question.choice && question.choice.autoAssigned && !boxData.currentBox.boxRevealed)
      ) {
        items.push(surpriseItem);
      } else if (question.choice) {
        question.choice.questionId = questionId;
        question.choice.grouping = grouping;
        question.choice.extraChoicePrice = extraChoicePrice;
        items.push(question.choice);
        if (question.additionalItems) {
          for (const additionalItem of question.additionalItems) {
            additionalItem.questionId = questionId;
            additionalItem.grouping = grouping;
            additionalItem.extraChoicePrice = extraChoicePrice;
            items.push(additionalItem);
          }
        }
      }
    }
    return items;
  };

  getPageItems = (boxData, userData) => {
    const { currentPage, pageLength } = this.state;
    const items = this.getItems(boxData, userData);

    const start = (currentPage - 1) * pageLength;
    const end = start + pageLength;

    return items.slice(start, end);
  };

  showOrderSummary = (boxData) => {
    if (boxData.currentBox) {
      return boxData.currentBox.boxStatus !== boxStates.ASSIGNED;
    }
    return false;
  };

  render() {
    const { sysdata, universalNavigationContext } = this.props;
    const { boxData = {}, userData = {}, isMobile, revealCallback } = sysdata;
    const { isNewMember } = universalNavigationContext;
    const isSelect = isSelectMember(userData);
    const isExpired = boxExpired(userData);
    const isClubTier = isClubMember(userData);
    const status = this.getOpenStatus(boxData);
    const cartSum = this.getExtraSum(boxData);
    const items = this.getItems(boxData, userData);
    const { pageLength, initialDisplayAmount } = this.state; // @TODO Get this number from the CMS
    const pageItems = this.getPageItems(boxData, userData);
    const showCountdown =
      boxData.currentBox &&
      !isExpired &&
      /**
       * show countdown on:
       * PRE -> Both customize and add-ons are closed before opening
       * ADD_ONS -> Customize is closed and add-ons is open, this happens 1 day before customize opens
       */
      ['PRE', 'ADD_ONS'].some((x) => x === boxData.season.campaignPhase);
    let participating = false;
    items.forEach((item) => {
      if (item.grouping === 'choice' && item.id !== 0) {
        participating = true;
      }
    });

    const displayViewButton = !isMobile && items.length > initialDisplayAmount;
    const displayUpsellCta = !isSelect && status === campaignState.OPEN;
    const displayOrderSummary = this.showOrderSummary(boxData);
    const displaySidebar = displayUpsellCta || displayOrderSummary;

    const buttonCTA = !this.state.viewAll ? 'View All' : 'View Less';

    return (
      <div>
        <div className={classNames.header}>
          <h1
            ref={(section) => {
              this.Header = section;
            }}
            className={cx(classNames.title, {
              [classNames.mobile]: isMobile,
            })}
            data-testid="customizations-title"
          >
            Customizations
          </h1>
          <div className={cx({ [classNames.statusContainer]: isMobile })}>
            <div className={classNames.status} data-testid="customizations-status">
              Status: {status}
              <div className={cx(classNames.statusLight, classNames[status])}>&nbsp;</div>
            </div>
          </div>
        </div>
        {showCountdown && (
          <Pre
            openDate={boxData.season.customizeStartDate}
            isMobile={isMobile}
            timerRefresh={revealCallback}
          />
        )}
        {/* Season is closed and the user has no items */}
        {!participating && !isExpired && status === campaignState.CLOSED && !showCountdown && (
          <ClosedNonParticipant isMobile={isMobile} isExpired={isExpired} />
        )}

        {/* Season is open but the user has no items */}
        {status === campaignState.OPEN && !participating && !isExpired && (
          <NoItemsCTA
            title="Customizations are Open!"
            desc="This is where you'll see your box customizations after you've selected them."
            ctaLabel="Customize Now"
            isMobile={isMobile}
            onClick={() => {
              window.location.href = `${env.envURL}/${isNewMember ? 'new-member-customize' : 'customize'}`;
              analytics.track(events.CLICKED_ELEMENT, {
                element_name: 'Customize Now',
                option_title: 'Customizations',
              });
            }}
          />
        )}

        {/* User's Tier is 'CLUB' and Customization campaign is 'CLOSED' */}
        {isClubTier && status === campaignState.CLOSED && (
          <ReactivateCTA
            baseClass="customize"
            title="Sorry, you have to be an active member to gain access to customizations!"
            desc="This is where you'll see your box customizations after you've selected them."
            ctaLabel="Activate My Membership"
            iconClass="fa-hand-holding-box"
            isMobile={isMobile}
            isClubTier
          />
        )}

        {/* User's Subscription is Expired */}
        {isExpired && !isClubTier && (
          <ReactivateCTA
            baseClass="customize"
            title="Sorry, you have to be an active member to gain access to customizations!"
            desc="This is where you'll see your box customizations after you've selected them."
            ctaLabel="Reactivate Now"
            iconClass="fa-hand-holding-box"
            isMobile={isMobile}
          />
        )}

        {/* Season is open and the user has items */}
        {!isExpired && participating && (
          <NotExpiredOpenParticipant
            displaySidebar={displaySidebar}
            isExpired={isExpired}
            isMobile={isMobile}
            items={items}
            viewAll={this.state.viewAll}
            initialDisplayAmount={initialDisplayAmount}
            isSelect={isSelect}
            displayOrderSummary={displayOrderSummary}
            getExtraChoiceCount={this.getExtraChoiceCount}
            boxData={boxData}
            cartSum={cartSum}
            userData={userData}
            displayViewButton={displayViewButton}
            displayUpsellCta={displayUpsellCta}
            toggleViewAll={() => this.setState({ viewAll: !this.state.viewAll })}
            status={status}
            pageItems={pageItems}
          />
        )}
        {!displaySidebar && displayViewButton && (
          <Button
            width="200px"
            style={{ margin: '16px auto', display: 'block' }}
            onClick={() => {
              this.setState({ viewAll: !this.state.viewAll });
              analytics.track(events.CLICKED_ELEMENT, {
                element_name: buttonCTA,
                option_title: 'Customizations',
              });
            }}
          >
            {buttonCTA}
          </Button>
        )}
        {isMobile && items.length > pageLength && !isExpired && (
          <PageNav
            maxPageCount={4}
            currentPage={this.state.currentPage}
            totalPages={Math.ceil(items.length / pageLength)}
            onChange={(pg) => {
              this.setState({ currentPage: pg });
              scrollToComponent(this.Header, {
                offset: 0,
                align: 'top',
                duration: 500,
              });
            }}
          />
        )}
        <div className="hor-rule mar-h-40" />
      </div>
    );
  }
}

export default withUniversalNavigationContext(CustomizeView);
